import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { AuthGuard } from './services/auth-guard';

import { LoginComponent } from './login/login.component';
import { WellComponent } from './wells/well.component';
import { WellCreationComponent } from './wells/well-creation/well-creation.component';
import { SettingsComponent } from './settings/settings.component';
import { SignupComponent } from './invite/signup.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { TruckingComponent } from './trucking/trucking.component';
import { CompletedOrdersComponent } from './completed-orders/completed-orders.component';
import { OrderTableComponent } from './completed-orders/order-table/order-table.component';
import { MinesComponent } from './mines/mines.component';
import { SandiPageComponent } from './ui-components/sandi-page/sandi-page.component';
import { NotificationPreferencesComponent } from './notification-preferences/notification-preferences.component';
import { UnsubscribeComponent } from './unsubscribe/unsubscribe.component';
import { AdminGuard } from '~services/admin-guard';
import { BulkExportComponent } from './bulk-export/bulk-export.component';
import { AdminRouteGuard } from '~services/admin-route-guard';
import * as fromConstants from './app-routing.constants';
import { BillingOrdersComponent } from './completed-orders/billing-orders/billing-orders.component';
import { AllFracOrderTableComponent } from './completed-orders/all-frac-order-table/all-frac-order-table.component';
import { UpdatePasswordComponent } from './update-password/update-password.component';
import { SigninWithCustomTokenLohiComponent } from './signin-with-custom-token-lohi/signin-with-custom-token-lohi.component';
import { BulkExportRouteGuard } from '~services/bulk-export-route-guard';
import { AdminOrStandardRouteGuard } from '~services/admin-or-standard-route-guard';
import { EmailDetailsV2Component } from './email-settings-v2/email-details-v2/email-details-v2.component';
import { EmailSettingsV2Component } from './email-settings-v2/email-settings-v2.component';
import { UnsubscribePoComponent } from './unsubscribe/unsubscribe-po/unsubscribe-po.component';
import { BonusEventComponent } from './bonus-event/bonus-event.component';
import { CreateBonusEventComponent } from './bonus-event/create-bonus-event/create-bonus-event.component';
import { LoaderAnalyticsDashboardV2Component } from './email-settings-v2/loader-analytics-dashboard-v2/loader-analytics-dashboard-v2/loader-analytics-dashboard-v2.component';
import { LoadsOnDateComponent } from './email-settings-v2/loader-analytics-dashboard-v2/loads-on-date/loads-on-date.component';
import { MatchesForLoadComponent } from './email-settings-v2/loader-analytics-dashboard-v2/matches-for-load/matches-for-load.component';
import { LoadsForPurchaseOrderComponent } from './email-settings-v2/loader-analytics-dashboard-v2/loads-for-purchase-order/loads-for-purchase-order.component';
import { BonusEnabledRouteGuard } from '~services/bonus-enabled-route-guard';
import { BonusEventArchivedComponent } from './bonus-event/bonus-event-archived/bonus-event-archived.component';
import { SubcarrierVendorContractsComponent } from './trucking/lmo-subcarrier-container/subcarrier-vendor-contracts/subcarrier-vendor-contracts.component';
import { LmoSubcarrierContainerComponent } from './trucking/lmo-subcarrier-container/lmo-subcarrier-container.component';
import { BoxPickupSitesComponent } from './box-pickup-sites/box-pickup-sites.component';
import { BonusEventActiveComponent } from './bonus-event/bonus-event-active/bonus-event-active.component';
import { AdminDispatcherStandardRouteGuard } from '~services/admin-dispatcher-standard-route-guard';
import { BulkExportV2Component } from './bulk-export-v2/bulk-export-v2.component';
import { TrailerManagementComponent } from './trailer-management/trailer-management.component';
import { ArchivedWellsComponent } from './wells/archived-wells/archived-wells.component';

const routes: Routes = [
  {
    loadChildren: () =>
      import('src/app/buyer-marketplace/buyer-marketplace.module').then((m) => m.BuyerMarketplaceModule),
    path: 'marketplace',
    canActivate: [AdminRouteGuard],
  },
  {
    loadChildren: () => import('src/app/restrictions/restrictions.module').then((m) => m.RestrictionsModule),
    path: 'restrictions',
    canActivate: [AdminRouteGuard],
  },
  {
    loadChildren: () => import('src/app/billing-2020/billing-2020.module').then((m) => m.Billing2020Module),
    path: 'invoicing',
    canActivate: [AdminRouteGuard],
  },
  {
    canActivate: [AdminRouteGuard],
    loadChildren: () => import('src/app/billing/billing.module').then((m) => m.BillingModule),
    path: 'billing',
  },
  {
    canActivate: [AuthGuard],
    loadChildren: () => import('src/app/lmo/lmo.module').then((m) => m.LmoModule),
    path: 'lmo',
  },
  {
    loadChildren: () => import('src/app/admin/admin.module').then((m) => m.AdminModule),
    canActivate: [AdminGuard],
    path: 'admin',
  },
  {
    loadChildren: () => import('src/app/api-docs/api-docs.module').then((m) => m.ApiDocsModule),
    canActivate: [AdminGuard],
    path: 'api',
  },
  {
    component: SigninWithCustomTokenLohiComponent,
    path: 'lohi/:jwt',
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'bulk-export',
    canActivate: [AuthGuard, BulkExportRouteGuard],
    component: BulkExportComponent,
  },
  {
    path: 'bulk-export-v2',
    canActivate: [AuthGuard, BulkExportRouteGuard],
    component: BulkExportV2Component,
  },
  {
    path: 'trailer-management',
    canActivate: [AuthGuard],
    component: TrailerManagementComponent,
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  {
    path: 'wells',
    canActivate: [AuthGuard, AdminDispatcherStandardRouteGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'archived',
      },
      {
        path: 'edit/:id',
        component: WellCreationComponent,
      },
      {
        path: 'edit/new',
        component: WellCreationComponent,
      },
      {
        path: 'archived',
        component: ArchivedWellsComponent,
      },
    ],
  },
  {
    path: 'loaders',
    canActivate: [AuthGuard, AdminRouteGuard],
    component: MinesComponent,
  },
  {
    path: 'notifications',
    canActivate: [AuthGuard, AdminOrStandardRouteGuard],
    component: NotificationPreferencesComponent,
  },
  {
    path: 'box-pickup-sites',
    canActivate: [AuthGuard, AdminOrStandardRouteGuard],
    component: BoxPickupSitesComponent,
  },
  {
    path: 'fracs/:id',
    canActivate: [AuthGuard, AdminRouteGuard],
    children: [
      {
        path: '',
        redirectTo: 'orders',
        pathMatch: 'full',
      },
      {
        path: 'orders',
        component: CompletedOrdersComponent,
        children: [
          {
            path: ':tab',
            component: OrderTableComponent,
            children: [
              {
                path: ':orderId',
                component: OrderTableComponent,
              },
            ],
          },
        ],
      },
      {
        path: 'market',
        loadChildren: () => import('src/app/marketplace/marketplace.module').then((m) => m.MarketplaceModule),
      },
    ],
  },
  {
    path: 'billing/fracs',
    component: BillingOrdersComponent,
    canActivate: [AdminRouteGuard],
    children: [
      {
        path: ':tab',
        component: AllFracOrderTableComponent,
        children: [
          {
            path: ':fracId/:orderId',
            component: AllFracOrderTableComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'market',
    loadChildren: () => import('src/app/marketplace/marketplace.module').then((m) => m.MarketplaceModule),
  },
  {
    path: 'users',
    component: SettingsComponent,
    canActivate: [AuthGuard, AdminRouteGuard],
  },
  {
    path: 'loader-integration-v2',
    component: EmailSettingsV2Component,
    canActivate: [AuthGuard, AdminRouteGuard],
  },
  {
    path: 'loader-integration-v2/detail/:id',
    component: EmailDetailsV2Component,
    canActivate: [AuthGuard, AdminRouteGuard],
  },
  {
    path: 'loader-analytics-dashboard-v2',
    component: LoaderAnalyticsDashboardV2Component,
    canActivate: [AuthGuard, AdminRouteGuard],
  },
  {
    path: `loader-analytics-dashboard-v2/email/:${fromConstants.LOADER_INTEGRATION_MINE_ID}/:${fromConstants.LOADER_INTEGRATION_DATE}`,
    component: LoadsOnDateComponent,
    canActivate: [AuthGuard, AdminRouteGuard],
  },
  {
    path: `loader-analytics-dashboard-v2/purchase-order/:${fromConstants.LOADER_INTEGRATION_PURCHASE_ORDER_ID}`,
    component: LoadsForPurchaseOrderComponent,
    canActivate: [AuthGuard, AdminRouteGuard],
  },
  {
    path: `loader-analytics-dashboard-v2/load/:${fromConstants.LOADER_INTEGRATION_LOADER_DATA_ID}`,
    component: MatchesForLoadComponent,
    canActivate: [AuthGuard, AdminRouteGuard],
  },
  {
    path: 'update-password',
    component: UpdatePasswordComponent,
    canActivate: [AuthGuard],
  },
  {
    path: `trucking-contracts`,
    canActivate: [AuthGuard, AdminRouteGuard],
    children: [
      {
        component: TruckingComponent,
        path: `:${fromConstants.TRUCKING_CONTRACT_ID}`,
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'list',
      },
    ],
  },
  {
    path: `subcarrier-contracts`,
    canActivate: [AuthGuard, AdminRouteGuard],
    component: SubcarrierVendorContractsComponent,
  },
  {
    path: `subcarrier`,
    canActivate: [AuthGuard, AdminRouteGuard],
    component: LmoSubcarrierContainerComponent,
  },
  {
    path: 'invite/:code',
    component: SignupComponent,
  },
  {
    path: 'sandi',
    component: SandiPageComponent,
  },
  {
    path: 'unsubscribe/email/:uuid',
    component: UnsubscribeComponent,
  },
  {
    path: 'unsubscribe/po/email/:uuid',
    component: UnsubscribePoComponent,
  },
  {
    path: 'bonus-event',
    canActivate: [AuthGuard, AdminRouteGuard, BonusEnabledRouteGuard],
    children: [
      {
        path: '',
        component: BonusEventComponent,
      },
      {
        path: ':type',
        component: BonusEventComponent,
      },
      {
        path: 'form/:type',
        component: CreateBonusEventComponent,
      },
      {
        path: `form/:type/:${fromConstants.GOHAWK_BONUS_EVENT_ID}`,
        component: CreateBonusEventComponent,
      },
    ],
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
